import { inject, Injectable, Injector, runInInjectionContext } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, distinctUntilChanged, filter, map, merge, switchMap, tap } from 'rxjs';

import { createProviderToken, filterBoolean } from '@core/common-legacy';
import { injectUntilDestroyed, stableStringify } from '@core/utils-legacy';
import { getStoreGroupId, ViewGroupsSelectors } from '@core/views-data';
import type { AnyGroup, ViewRecord, GroupingState } from '@core/views-types';
import type { DataTransform } from '@core/views-util';
import { dataSource$, NOOP_TRANSFORM, TABLE_CONFIGS } from '@core/views-util';

import { ViewDataService } from '../../../view-data.service';
import { ViewsLocalStorageService } from '../../local-storage.service';
import { ViewPageService } from '../../view-page.service';
import { LayoutSharedService } from '../layout-shared.service';

export const {
  provide: provideLayoutTableDefaultPageSize,
  inject: injectLayoutTableDefaultPageSize,
} = createProviderToken('LayoutTableDefaultPageSize')(false, {
  factory: () => 200,
});

@Injectable()
export class LayoutTableDataService {
  public readonly GROUP_PAGE_SIZE = 10;
  public readonly RECORDS_DEFAULT_PAGE_SIZE = injectLayoutTableDefaultPageSize();
  public records$ = inject(LayoutSharedService).records$.pipe(
    map(records => this.recordTransformer.transform(<any>records)),
  );
  public groupsState?: GroupingState;
  public tableMeta$ = new BehaviorSubject<Record<string, { page: number; limit: number }>>({});

  private untilDestroyed = injectUntilDestroyed();
  private viewDataService = inject(ViewDataService);
  private viewPageService = inject(ViewPageService);
  private store = inject(Store);
  private injector = inject(Injector);
  private layoutSharedService = inject(LayoutSharedService);
  private localStorageService = inject(ViewsLocalStorageService);
  private tableConfigsMap = inject(TABLE_CONFIGS);
  private dataSource$ = inject(dataSource$);
  private tableConfig$ = this.dataSource$.pipe(
    map(dataSource => this.tableConfigsMap[dataSource.id]!),
  );
  private recordTransformer: DataTransform<ViewRecord, ViewRecord>;

  constructor() {
    this.tableConfig$.pipe(this.untilDestroyed).subscribe(tableConfig => {
      runInInjectionContext(this.injector, () => {
        this.recordTransformer = tableConfig.recordsTransform
          ? inject(tableConfig.recordsTransform)
          : NOOP_TRANSFORM;
      });
    });
    this.viewDataService.groupState$.pipe(this.untilDestroyed).subscribe(groupState => {
      this.groupsState = groupState;
    });
    this.watchParamsChanges();
    this.loadInitialRecords();
    this.handleGroupChanges();
    this.saveRestoreTableGeometry();
  }

  public loadGroups() {
    const { groupingIdControl } = this.viewDataService;
    if (!groupingIdControl.value) return;

    const [groupingId, groupsState] = this.groupsState ?? [groupingIdControl.value, null];
    if (!groupsState) return;
    const storeGroupId = getStoreGroupId(this.viewDataService.dataSourceId, groupingId!, null);
    const groupsMeta = this.store.selectSignal(ViewGroupsSelectors.meta(storeGroupId))();
    const canLoadMore =
      groupsState.meta.totalCount > groupsState.meta.page * groupsState.meta.limit;
    if (groupsMeta?.isPending || groupsMeta?.isFailed || !canLoadMore) return;

    if (groupingId !== groupingIdControl.value) {
      this.viewDataService.loadGroups(1, this.GROUP_PAGE_SIZE);
    } else if (groupingId === groupingIdControl.value) {
      this.viewDataService.loadGroups((groupsState.meta.page ?? 0) + 1, this.GROUP_PAGE_SIZE);
    }
  }

  public loadRecords(group: AnyGroup | null, options: { nextPage?: number; nextLimit?: number }) {
    const groupingId = this.viewDataService.groupingIdControl.value;
    const params = this.viewDataService.getFormParams();

    this.viewDataService.loadRecords(
      {
        ...params,
        group: groupingId ? { configId: groupingId, value: group!.id } : null,
        layout: null,
      },
      {
        page: options.nextPage ?? 1,
        limit: options.nextLimit ?? this.RECORDS_DEFAULT_PAGE_SIZE,
      },
      {
        concat: !options.nextLimit,
      },
    );
  }

  private watchParamsChanges() {
    merge(
      this.viewDataService.routerViewId$.pipe(
        switchMap(viewId =>
          this.viewPageService.currentView$.pipe(filter(view => view?.id === viewId)),
        ),
      ),
      this.viewDataService.formParams$.pipe(filter(({ layout }) => layout.type === 'table')),
    )
      .pipe(this.untilDestroyed)
      .subscribe(() => {
        this.viewDataService.clearRecords();
        this.viewDataService.clearGroups();
      });
  }

  private loadInitialRecords() {
    this.records$.pipe(this.untilDestroyed).subscribe(({ groupingId, records }) => {
      if (groupingId !== this.viewDataService.groupingIdControl.value) return;

      records.forEach(record => {
        const groupId = stableStringify(record.groupLayout?.trackingId);
        if (record.groupLayout?.isHidden) return;

        if (!record.meta?.isLoaded && !record.meta?.isPending) {
          const params = this.viewDataService.getFormParams();
          const limit = this.tableMeta$.value[groupId]?.limit ?? this.RECORDS_DEFAULT_PAGE_SIZE;
          this.viewDataService.loadRecords(
            {
              ...params,
              group: groupingId ? { configId: groupingId, value: record.group!.id } : null,
            },
            { page: 1, limit },
          );
        }
      });
    });
  }

  private handleGroupChanges() {
    this.viewDataService.formParams$
      .pipe(
        map(({ group }) => ({ configId: group?.configId, hideEmpty: group?.hideEmpty })),
        distinctUntilChanged(),
        this.untilDestroyed,
      )
      .subscribe(({ configId }) => {
        if (!configId) return;

        this.viewDataService.loadGroups(1, this.GROUP_PAGE_SIZE);
      });
  }

  private saveRestoreTableGeometry() {
    const { viewForm } = this.viewDataService;
    const currentViewId$ = this.viewPageService.currentView$.pipe(
      map(view => view?.id),
      filterBoolean,
      distinctUntilChanged(),
    );
    currentViewId$
      .pipe(
        switchMap(viewId => {
          const lsTableMeta = this.localStorageService.tablePagination.get(viewId) ?? {};
          const tableMeta = Object.fromEntries(
            Object.entries(lsTableMeta).map(([groupId, meta]) => [groupId, { ...meta, page: 1 }]),
          );
          this.tableMeta$.next(tableMeta);
          return merge(
            viewForm.controls.group.controls.configId.valueChanges.pipe(
              tap(() => this.tableMeta$.next({})),
            ),
            this.tableMeta$.pipe(
              tap(tableMeta => {
                const toSaveMeta = Object.fromEntries(
                  Object.entries(tableMeta).map(([groupId, meta]) => [
                    groupId,
                    { limit: meta.limit },
                  ]),
                );
                this.localStorageService.tablePagination.set(viewId, toSaveMeta);
              }),
            ),
          );
        }),
        this.untilDestroyed,
      )
      .subscribe();
  }
}
